/**
 * qFlow's filters, read from and written into a `TraceQuery`: the workflow is
 * `traceNames`, the version `version`, and the rest are columns the backend's
 * qFlow reader knows (`status`, `is_test`, `sample`, `node.<id>.<field>`).
 * Pure, no React.
 */

import type { TraceQuery } from "./trace-query";

export const STATUSES = ["succeeded", "failed", "partial_failure", "cancelled", "running", "started"];

/** Real runs only (qFlow's default), test runs only, or both. */
export type RunKind = "false" | "true" | "any";

export interface NodeRule {
  node: string;
  field: string;
  value: string;
}

const NODE = "node.";

export const runKind = (q: TraceQuery): RunKind => {
  const v = q.columns.is_test?.[0];
  return v === "true" || v === "any" ? v : "false";
};

/** `q` with `column` set to `values`, or dropped when there are none. */
export function withColumn(q: TraceQuery, column: string, values: string[]): TraceQuery {
  const { [column]: _dropped, ...rest } = q.columns;
  return { ...q, columns: values.length ? { ...rest, [column]: values } : rest };
}

export const withRunKind = (q: TraceQuery, kind: RunKind) =>
  withColumn(q, "is_test", kind === "false" ? [] : [kind]);

/**
 * Each `node.<id>.<field>` column, one rule per value. As on the backend, the
 * node id is the first dot part (qFlow ids are never dotted) and the field is
 * the rest, a path into nested output like `result.label`.
 */
export function nodeRules(q: TraceQuery): NodeRule[] {
  return Object.entries(q.columns).flatMap(([column, values]) => {
    if (!column.startsWith(NODE)) return [];
    const path = column.slice(NODE.length);
    const dot = path.indexOf(".");
    if (dot <= 0) return [];
    return values.map((value) => ({ node: path.slice(0, dot), field: path.slice(dot + 1), value }));
  });
}

/** A dot path into a node's output, as the backend reads it: no empty part (`result.`, `.label`, `a..b`). */
export const isFieldPath = (field: string) => field.trim().split(".").every(Boolean);

/** `q` with its node columns replaced by `rules`. Half-filled rules, dotted node ids and broken paths are left out. */
export function withNodeRules(q: TraceQuery, rules: NodeRule[]): TraceQuery {
  const columns = Object.fromEntries(Object.entries(q.columns).filter(([c]) => !c.startsWith(NODE)));
  for (const r of rules) {
    const node = r.node.trim();
    const field = r.field.trim();
    if (!node || node.includes(".") || !isFieldPath(field) || !r.value.trim()) continue;
    const key = `${NODE}${node}.${field}`;
    columns[key] = [...(columns[key] ?? []), r.value.trim()];
  }
  return { ...q, columns };
}

/** The chips a saved qFlow source shows; `names` gives a workspace's name by id. */
export function qflowChips(q: TraceQuery, names: Record<string, string> = {}): string[] {
  const kind = runKind(q);
  const workspace = q.environments[0];
  return [
    ...(workspace ? [`Workspace: ${names[workspace] ?? workspace}`] : []),
    ...(q.traceNames.length ? [`Workflow: ${q.traceNames.join(", ")}`] : []),
    ...(q.version ? [`Version: ${q.version}`] : []),
    ...(q.columns.status?.length ? [`Status: ${q.columns.status.join(", ")}`] : []),
    kind === "true" ? "Test runs" : kind === "any" ? "Real and test runs" : "Real runs",
    ...(q.columns.sample?.[0] ? [`Random ${q.columns.sample[0]}`] : []),
    ...nodeRules(q).map((r) => `${r.node}.${r.field} = ${r.value}`),
    ...(q.skipFailed ? ["Skips failed"] : []),
  ];
}
